import React, {type ReactElement, useEffect, useRef, useState} from 'react';

import cs from 'classnames';
import {colors} from 'js-libs/sweetcommons/styles';
import debounce from 'lodash/debounce';
import ReactDOMServer from 'react-dom/server';
import styled from 'styled-components';
import Tribute from 'tributejs';

import {showNotification} from 'app/toasts/slice';
import {
  fetchUserList,
  requestUserListPermissionByDocument,
} from 'app/user/actions';
import {gt} from 'app/utils/i18n';
import {useAsyncActions} from 'app/utils/redux';

import type {UserPermissionsSerializerType} from 'app/current-user/types';
import type {CommentContentType} from 'sweet-types';

type Props = {
  contentType: CommentContentType;
  objectId: string | number;
  onChange: (text: string) => void;
  onSubmit: (
    event: React.KeyboardEvent<HTMLDivElement>,
    currentComment?: string
  ) => void;
  value: string;
  children: (arg0: {
    Field: typeof ContentEditable;
    fieldProps: {
      ref: {
        current: HTMLElement | null;
      };
      value: string;
      onChange: (arg0: string) => void;
      onSubmit: (
        event: React.KeyboardEvent<HTMLDivElement>,
        currentComment?: string
      ) => void;
    };
    mentionsMenu: ReactElement<typeof StyledMenuDiv>;
  }) => JSX.Element;
};
export function Mentionable({
  children,
  value,
  onChange,
  onSubmit,
  contentType,
  objectId,
}: Props) {
  const actions = useAsyncActions({
    fetchUserList,
    showNotification,
  });
  const [menuNode, setMenuNode] = useState<null | HTMLElement>(null);
  const tribute = useRef<Tribute<UserPermissionsSerializerType>>();
  const inputRef = useRef<null | HTMLElement>(null);
  useEffect(() => {
    if (menuNode != null && inputRef.current != null) {
      const teammatesGetter = debounce(
        (search: string, callback: (arg0: Array<any>) => void) => {
          if (search === '') {
            return requestUserListPermissionByDocument(objectId, contentType, {
              with_permissions: 1,
              page_size: 10,
            }).then((data) => callback(data.results));
          }

          Promise.all([
            actions
              .fetchUserList({
                search,
                page_size: 10,
              })
              .then((data) => data.response.results),
            requestUserListPermissionByDocument(objectId, contentType, {
              search,
              page_size: 10,
            }).then((data) => data.results),
          ]).then(([users, permissions]) => {
            const permMap = permissions.reduce((map, user) => {
              map[user.id] = user.can_view;
              return map;
            }, {});
            callback(
              users.map((user) => ({
                ...user,
                can_view: permMap[user.id] === true,
              }))
            );
          });
        },
        500
      );
      tribute.current = new Tribute<UserPermissionsSerializerType>({
        values: teammatesGetter,
        positionMenu: false,
        lookup: 'name',
        fillAttr: 'name',
        menuContainer: menuNode,
        menuItemTemplate: ({original: {name, email, can_view}}) =>
          ReactDOMServer.renderToStaticMarkup(
            <div className={!can_view ? 'disabled' : ''}>{name || email}</div>
          ),
        selectTemplate: (item: {original: UserPermissionsSerializerType}) => {
          const {
            original: {name, email, id, can_view},
          } = item;

          if (!can_view) {
            actions.showNotification(
              gt(
                'That teammate cannot receive a mention because they do not have access to this document'
              ),
              'warning'
            );
          }

          return can_view
            ? ReactDOMServer.renderToStaticMarkup(
                <a data-user-id={id}>@{name || email}</a>
              )
            : '';
        },
      });
      tribute.current.attach(inputRef.current);
    }
  }, [menuNode, inputRef.current]);
  return children({
    Field: ContentEditable,
    fieldProps: {
      ref: inputRef,
      value,
      onChange,
      onSubmit,
    },
    mentionsMenu: <StyledMenuDiv ref={(node) => setMenuNode(node)} />,
  });
}
type ContentEditableProps = {
  onChange: (arg0: string) => void;
  value: string;
  disabled?: boolean;
  forwardedRef: any;
  onSubmit: (
    event: React.KeyboardEvent<HTMLDivElement>,
    currentComment?: string
  ) => void;
  autoFocus?: boolean;
  className?: string;
  placeholder?: string;
  style?: Record<string, any>;
};

const BaseContentEditable = ({
  value,
  onChange,
  forwardedRef,
  onSubmit,
  autoFocus,
  className,
  placeholder,
  style,
  disabled = false,
}: ContentEditableProps) => {
  const divRef = React.createRef<HTMLDivElement>();
  const cachedValue = useRef(value);

  const getCurrentHTML = () => {
    return divRef.current != null ? divRef.current.innerHTML : '';
  };

  useEffect(() => {
    if (typeof forwardedRef === 'function') {
      forwardedRef(divRef.current);
    } else {
      forwardedRef.current = divRef.current;
    }

    if (autoFocus === true) {
      setTimeout(() => {
        if (divRef.current != null) {
          divRef.current.focus();
        }
      }, 0);
    }
  }, []);

  useEffect(() => {
    if (divRef.current != null && getCurrentHTML() !== value) {
      divRef.current.innerHTML = value;
    }
  }, [value]);

  const interceptEnter = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && !event.shiftKey && !disabled) {
      onSubmit(event, getCurrentHTML());
    }
  };

  const emitChange = () => {
    let newValue = getCurrentHTML();
    if (divRef.current != null && disabled) {
      divRef.current.innerHTML = value;
    }
    if (value !== newValue && !disabled) {
      onChange(newValue);
    }
  };

  return (
    <StyledWithMentionsDiv
      onSubmit={onSubmit}
      className={cs(className, {
        'disabled list-group-item-light opacity-50': disabled,
      })}
      placeholder={placeholder}
      style={style}
      ref={divRef}
      onInput={emitChange}
      onBlur={emitChange}
      onKeyDown={interceptEnter}
      contentEditable={!disabled}
      dangerouslySetInnerHTML={{
        __html: cachedValue.current,
      }}
    />
  );
};

export const ContentEditable = React.forwardRef<
  HTMLDivElement,
  Omit<ContentEditableProps, 'forwardedRef'>
>((props, ref) => <BaseContentEditable {...props} forwardedRef={ref} />);

export const StyledWithMentionsDiv = styled.div`
  a[data-user-id] {
    &,
    &:hover {
      padding: 2px 0;
      margin: -2px 0;
      background: ${colors.paleBlue};
      color: ${colors.linkBlue};
    }
  }
`;
const StyledMenuDiv = styled.div`
  position: relative;

  .tribute-container {
    position: absolute;
    left: 0;
    bottom: 0;
    width: 100%;
    max-height: 70vh;
    overflow: auto;
    display: block;
    z-index: 5;

    ul {
      margin: 0;
      margin-top: 2px;
      padding: 0;
      list-style: none;
      background: #fff;
      border-top: 1px solid #ccc;

      li {
        cursor: pointer;
        font-size: 90%;

        &.highlight {
          background: ${colors.selectedBg};
          color: #fff;
        }

        &.no-match {
          cursor: default;
        }

        > div {
          padding: 5px 5px;
        }

        &:not(.highlight) > div.disabled {
          color: #999;
        }

        &.highlight > div.disabled {
          background: #999;
        }
      }
    }
  }
`;
